import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UsageData {
  llm_outputs: number;
  agents: number;
  leads: number;
  whatsapp: number;
  instagram: number;
  scrapes: number;
}

interface LimitsData {
  llm_outputs: number;
  agents: number;
  leads: number;
  whatsapp: number;
  instagram: number;
  extra_message_price: number;
  scrapes: number;
}

export interface WorkspaceUsageResult {
  usage: UsageData;
  limits: LimitsData;
  plan: string;
  period_start: string;
  period_end: string;
}

export function getUsagePercentage(used: number, max: number): number {
  if (max <= 0) return used > 0 ? 100 : 0;
  return Math.min(Math.round((used / max) * 100), 100);
}

export function getUsageRemaining(used: number, max: number): number {
  return Math.max(0, max - used);
}

export function isUsageOverLimit(used: number, max: number): boolean {
  return used > max;
}

export function isUsageNearLimit(used: number, max: number, threshold = 0.8): boolean {
  if (max <= 0) return false;
  return used / max >= threshold && used <= max;
}

export function useWorkspaceUsage(workspaceId: string | null) {
  const [usageData, setUsageData] = useState<WorkspaceUsageResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc(
        "get_workspace_usage_stats",
        { p_workspace_id: workspaceId }
      );

      if (rpcError) {
        console.error("Error fetching workspace usage:", rpcError);
        setError(rpcError.message);
        return;
      }

      if (data) {
        setUsageData(data as unknown as WorkspaceUsageResult);
      }
    } catch (err) {
      console.error("Error in useWorkspaceUsage:", err);
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, [workspaceId]);

  return {
    usageData,
    loading,
    error,
    refetch: fetchUsage,
    getPercentage: getUsagePercentage,
    isOverLimit: isUsageOverLimit,
    getRemaining: getUsageRemaining,
    isNearLimit: isUsageNearLimit,
  };
}
